import React, { useRef } from 'react';
import { StringPropertyDTO } from 'business/models/Models';
import { FmlxComboBox } from 'fmlx-common-ui';
import {
  isStandardPrintLineProperty,
  MAX_STANDARD_PRINT_LINE_TAGS,
  printLineParametersItems,
} from './constants';
import { buildPrintLineValue, selectedPrintLineParametersItems } from './utils';

type PrintLinePropertyInputProps = {
  prop: StringPropertyDTO;
  onChangeProp: (prop: StringPropertyDTO, e: { target: { value: string } }) => void;
};

const isInputRootBackground = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && target.classList.contains('MuiOutlinedInput-root');

export const PrintLinePropertyInput: React.FC<PrintLinePropertyInputProps> = ({
  prop,
  onChangeProp,
}) => {
  const handleDeleteChip = (
    deletedItemIdOrPayload: number | { id: number } | unknown,
  ) => {
    if (!prop?.value || typeof prop.value !== 'string') {
      return;
    }

    const deletedId =
      typeof deletedItemIdOrPayload === 'object' &&
      deletedItemIdOrPayload !== null &&
      'id' in deletedItemIdOrPayload
        ? (deletedItemIdOrPayload as { id: number }).id
        : typeof deletedItemIdOrPayload === 'number'
        ? deletedItemIdOrPayload
        : null;

    if (deletedId === null) {
      return;
    }

    const currentItems = selectedPrintLineParametersItems(prop, printLineParametersItems);
    const updatedItems = currentItems.filter((item) => item.id !== deletedId);

    handlePrintLineParametersChange(prop, updatedItems);
  };

  const selectedItems = selectedPrintLineParametersItems(prop, printLineParametersItems);
  const maxTags = isStandardPrintLineProperty(prop.name)
    ? MAX_STANDARD_PRINT_LINE_TAGS
    : undefined;
  const hasError = maxTags !== undefined && selectedItems.length > maxTags;
  const comboboxId = `fmlx-combo-box-print-line-parameters-${prop.name}`;
  const errorMessage =
    maxTags !== undefined ? `Print Label cannot exceed ${maxTags}` : '';
  // add a wrapper div around combobox that have ref to combobox
  const wrapperRef = useRef<HTMLDivElement>(null);

  // add a handler that check if the combobox is open and prevent it from closing and opening when clicking on the wrapper
  const handleWrapperClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isInputRootBackground(e.target)) {
      return;
    }

    const input = wrapperRef.current?.querySelector(
      `[id="${comboboxId}"] input[role="combobox"]`,
    ) as HTMLInputElement | null;

    if (input?.getAttribute('aria-expanded') === 'true') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handlePrintLineParametersChange = (propToUpdate: StringPropertyDTO, e: any) => {
    const ids = e
      .map((item: any) => {
        if (typeof item === 'number') {
          return item;
        }

        if (item && typeof item === 'object' && 'id' in item) {
          return item.id;
        }
        return null;
      })
      .filter((id): id is number => id !== null);

    const pipeSeparatedText = buildPrintLineValue(
      ids,
      propToUpdate.value || '',
      printLineParametersItems,
    );

    onChangeProp(propToUpdate, { target: { value: pipeSeparatedText } });
  };

  return (
    <div
      className={`flex-50 min-h-[2rem] relative print-line-combobox-wrapper ${
        hasError ? 'has-error' : ''
      }`}
      ref={wrapperRef}
      onClickCapture={handleWrapperClickCapture}
      onMouseEnter={(e) => {
        if (hasError) {
          const tooltip = document.getElementById(`${comboboxId}-tooltip`);
          if (tooltip) {
            const rect = e.currentTarget.getBoundingClientRect();
            tooltip.classList.remove('hidden');
            tooltip.style.left = `${rect.left}px`;
            tooltip.style.top = `${rect.top - 35}px`;
          }
        }
      }}
      onMouseLeave={() => {
        const tooltip = document.getElementById(`${comboboxId}-tooltip`);
        if (tooltip) {
          tooltip.classList.add('hidden');
        }
      }}
    >
      <FmlxComboBox
        id={comboboxId}
        size="sm"
        multiple
        placeholder={prop.value ? '' : 'Select Tags'}
        items={printLineParametersItems}
        selectedMultipleItem={selectedItems}
        onChange={(e) => handlePrintLineParametersChange(prop, e)}
        limitTags={2}
        onDeleteChip={handleDeleteChip}
      />

      {hasError && (
        <div
          id={`${comboboxId}-tooltip`}
          className="print-line-error-tooltip hidden fixed bg-[#474747] text-white px-3 py-2 rounded-[4px] text-xs z-[10000] pointer-events-none whitespace-nowrap shadow-lg"
        >
          {errorMessage}
          <div className="absolute -bottom-1.5 left-5 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#f44336]" />
        </div>
      )}
    </div>
  );
};
